import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  Link,
  redirect,
} from '@tanstack/react-router';
import { FileQuestion } from 'lucide-react';
import { AppShell } from '@/components/layout/app-shell';
import { SayfaHatasi } from '@/components/layout/hata-ekrani';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';

/**
 * §19.3 — Route bazlı kod bölme; ilk bundle < 250 KB gzip.
 *
 * Her ekran ayrı parçaya çıkar ve **yalnızca o sayfaya gidilince** indirilir.
 * Recharts gibi ağır kütüphaneler böylece ilk yüke girmez — grafik yalnızca
 * Kur Ayarları ekranında var.
 */

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
  notFoundComponent: () => (
    <EmptyState
      icon={FileQuestion}
      title="Sayfa bulunamadı"
      description="Aradığınız sayfa taşınmış veya henüz hazır değil olabilir."
      action={
        <Link to="/">
          <Button variant="secondary">Ana sayfaya dön</Button>
        </Link>
      }
    />
  ),
});

/** Rota tanımını kısaltan yardımcı — her ekran tembel yüklenir. */
function lazyRoute(
  path: string,
  loader: () => Promise<Record<string, unknown>>,
  exportName: string,
) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    component: lazyRouteComponent(loader as never, exportName as never),
  });
}

/** Taşınmış ekranın eski adresi: bildirim satırları ve e-postalar hâlâ bu adresi gösteriyor. */
function eskiAdres(path: string, yeniAdres: string) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: () => {
      throw redirect({ to: yeniAdres as never, replace: true });
    },
  });
}

const routes = [
  lazyRoute('/', () => import('@/features/dashboard/dashboard-page'), 'DashboardPage'),
  lazyRoute('/envanter', () => import('@/features/inventory/inventory-page'), 'InventoryPage'),
  lazyRoute('/siparisler', () => import('@/features/orders/orders-page'), 'OrdersPage'),
  lazyRoute(
    '/siparisler/$amazonOrderId',
    () => import('@/features/orders/order-detail-page'),
    'OrderDetailPage',
  ),
  lazyRoute(
    '/envanter/urun-sil',
    () => import('@/features/inventory/delete-products-page'),
    'DeleteProductsPage',
  ),
  lazyRoute(
    '/envanter/asin-kurallari',
    () => import('@/features/pricing/asin-rules-page'),
    'AsinRulesPage',
  ),
  lazyRoute(
    '/fiyatlandirma/kar-kurallari',
    () => import('@/features/pricing/profit-rules-page'),
    'ProfitRulesPage',
  ),
  lazyRoute(
    '/fiyatlandirma/simulasyon',
    () => import('@/features/pricing/simulation-page'),
    'SimulationPage',
  ),
  lazyRoute(
    '/fiyatlandirma/repricer',
    () => import('@/features/repricer/repricer-settings-page'),
    'RepricerSettingsPage',
  ),
  lazyRoute(
    '/fiyatlandirma/repricer-izleme',
    () => import('@/features/repricer/repricer-monitor-page'),
    'RepricerMonitorPage',
  ),
  lazyRoute(
    '/urun-arama/yeni',
    () => import('@/features/product-search/new-search-page'),
    'NewSearchPage',
  ),
  ...[
    '/raporlar/kategoriler',
    '/raporlar/markalar',
    '/raporlar/kar-analizi',
    '/raporlar/sorunlu-urunler',
  ].map((path) => lazyRoute(path, () => import('@/features/reports/reports-page'), 'ReportsPage')),
  lazyRoute(
    '/urun-arama/gecmis',
    () => import('@/features/product-search/search-history-page'),
    'SearchHistoryPage',
  ),
  lazyRoute(
    '/urun-arama/dogrudan-ekle',
    () => import('@/features/discovery/direct-add-page'),
    'DirectAddPage',
  ),
  lazyRoute(
    '/urun-arama/amazon-araclari',
    () => import('@/features/scrape-tools/amazon-tools-page'),
    'AmazonToolsPage',
  ),
  lazyRoute(
    '/urun-arama/bsr-tarayici',
    () => import('@/features/bsr-tarayici/bsr-tarayici-page'),
    'BsrTarayiciPage',
  ),
  lazyRoute(
    '/urun-arama/marka-tarayici',
    () => import('@/features/marka-tarayici/marka-tarayici-page'),
    'MarkaTarayiciPage',
  ),
  // Satıcı Sayısı Tarayıcı — Satıcısız ASIN Bulucu (29 Ekim 2026; sahibin Satici_Sayisi_Cekme programı)
  lazyRoute(
    '/urun-arama/satici-sayisi',
    () => import('@/features/satici-sayisi/satici-sayisi-page'),
    'SaticiSayisiPage',
  ),
  // Mağaza ASIN Tarayıcı — Amazon.sa mağazalarının ASIN'leri (29 Ekim 2026; sahibin Magaza_Tarama_Manuel programı)
  lazyRoute(
    '/urun-arama/magaza-tarayici',
    () => import('@/features/magaza-tarayici/magaza-tarayici-page'),
    'MagazaTarayiciPage',
  ),
  lazyRoute(
    '/urun-arama/bilgi-bankasi',
    () => import('@/features/discovery/asin-knowledge-page'),
    'AsinKnowledgePage',
  ),
  lazyRoute(
    '/urun-arama/havuz',
    () => import('@/features/product-search/upload-pool-page'),
    'UploadPoolPage',
  ),
  ...[
    '/guvenlik/markalar',
    '/guvenlik/urunler',
    '/guvenlik/kelimeler',
    '/guvenlik/kategoriler',
    '/guvenlik/saticilar',
  ].map((path) =>
    lazyRoute(path, () => import('@/features/blacklists/blacklist-page'), 'BlacklistPage'),
  ),
  lazyRoute(
    '/guvenlik/ortak-havuz',
    () => import('@/features/blacklists/common-pool-page'),
    'CommonPoolPage',
  ),
  lazyRoute('/ayarlar/kur', () => import('@/features/pricing/fx-page'), 'FxSettingsPage'),
  lazyRoute(
    '/ayarlar/envanter',
    () => import('@/features/settings/inventory-settings-page'),
    'InventorySettingsPage',
  ),
  lazyRoute(
    '/finans/kar-tablosu',
    () => import('@/features/kar-tablosu/kar-tablosu-page'),
    'KarTablosuPage',
  ),
  lazyRoute(
    '/finans/siparis-id',
    () => import('@/features/siparis-id/siparis-id-page'),
    'SiparisIdPage',
  ),
  lazyRoute('/finans/fatura', () => import('@/features/fatura/fatura-page'), 'FaturaPage'),
  lazyRoute(
    '/finans/ihlal-radari',
    () => import('@/features/ihlal-radari/ihlal-radari-page'),
    'IhlalRadariPage',
  ),
  lazyRoute(
    '/finans/yorum-tespiti',
    () => import('@/features/yorum-tespiti/yorum-tespiti-page'),
    'YorumTespitiPage',
  ),
  lazyRoute(
    '/ayarlar/guvenlik',
    () => import('@/features/settings/security-status-page'),
    'SecurityStatusPage',
  ),
  lazyRoute(
    '/ayarlar/amazon-baglantisi',
    () => import('@/features/settings/amazon-connection-page'),
    'AmazonConnectionPage',
  ),
  lazyRoute(
    '/ayarlar/magaza',
    () => import('@/features/integrations/store-integrations-page'),
    'StoreIntegrationsPage',
  ),
  lazyRoute(
    '/ayarlar/handling-time',
    () => import('@/features/handling-time/handling-time-page'),
    'HandlingTimePage',
  ),
  lazyRoute(
    '/ayarlar/kaynak',
    () => import('@/features/source/source-management-page'),
    'SourceManagementPage',
  ),
  lazyRoute(
    '/ayarlar/onceliklendirme',
    () => import('@/features/sync/sync-settings-page'),
    'SyncSettingsPage',
  ),
  lazyRoute('/ayarlar/kargo', () => import('@/features/shipping/cargo-page'), 'CargoPage'),
  lazyRoute('/envanter/kargo', () => import('@/features/shipping/cargo-page'), 'CargoPage'),
  // Eski bildirim ve e-postalardaki bağlantılar bu adresleri gösteriyor
  lazyRoute('/kargo', () => import('@/features/shipping/cargo-page'), 'CargoPage'),
  lazyRoute('/fiyatlandirma/kur', () => import('@/features/pricing/fx-page'), 'FxSettingsPage'),
  lazyRoute(
    '/kaynak/keepa',
    () => import('@/features/source/source-management-page'),
    'SourceManagementPage',
  ),
  lazyRoute('/sistem', () => import('@/features/system/system-status-page'), 'SystemStatusPage'),
  eskiAdres('/sorunlu-urunler', '/raporlar/sorunlu-urunler'),
  eskiAdres('/envanter/hazirlama-suresi', '/ayarlar/handling-time'),
  // 22 Eki 2026 — SellerFlash incelemesinden eklenen ekranlar
  lazyRoute(
    '/envanter/tekrarli',
    () => import('@/features/inventory/tekrarli-urunler-page'),
    'TekrarliUrunlerPage',
  ),
  lazyRoute(
    '/raporlar/cok-satanlar',
    () => import('@/features/reports/cok-satanlar-page'),
    'CokSatanlarPage',
  ),
  lazyRoute('/raporlar/buybox', () => import('@/features/reports/buybox-page'), 'BuyboxPage'),
  lazyRoute(
    '/ayarlar/panel-kopyala',
    () => import('@/features/settings/panel-kopyala-page'),
    'PanelKopyalaPage',
  ),
  lazyRoute('/islem-gecmisi', () => import('@/features/activity/activity-page'), 'ActivityPage'),
  lazyRoute(
    '/islem-gecmisi/envanter',
    () => import('@/features/activity/activity-page'),
    'ActivityInventoryPage',
  ),
  lazyRoute(
    '/islem-gecmisi/urun',
    () => import('@/features/activity/activity-page'),
    'ActivityProductPage',
  ),
  lazyRoute(
    '/islem-gecmisi/toplu',
    () => import('@/features/activity/activity-page'),
    'ActivityBulkPage',
  ),
  lazyRoute(
    '/raporlar/business',
    () => import('@/features/reports/business-report-page'),
    'BusinessReportPage',
  ),
  lazyRoute(
    '/raporlar/satis-trafik',
    () => import('@/features/reports/business-report-page'),
    'SalesTrafficPage',
  ),
  lazyRoute(
    '/raporlar/hesap-sagligi',
    () => import('@/features/reports/account-health-page'),
    'AccountHealthPage',
  ),
  lazyRoute(
    '/islem-gecmisi/feed',
    () => import('@/features/feeds/feed-history-page'),
    'FeedHistoryPage',
  ),
  lazyRoute(
    '/islem-gecmisi/repricer',
    () => import('@/features/activity/activity-page'),
    'ActivityRepricerPage',
  ),
  lazyRoute(
    '/ayarlar/bildirimler',
    () => import('@/features/notifications/notification-settings-page'),
    'NotificationSettingsPage',
  ),
  lazyRoute(
    '/ayarlar/sistem',
    () => import('@/features/system/system-status-page'),
    'SystemStatusPage',
  ),
  lazyRoute(
    '/ayarlar/kullanicilar',
    () => import('@/features/account/security-page'),
    'SecurityPage',
  ),
  /*
   * §31 — Harici araçlar. Bunlar YALNIZCA bağlantı sayfalarıdır; araçların
   * mantığı yazılıma taşınmadı (kullanıcının açık isteği).
   */
  lazyRoute(
    '/araclar/kargo-cekme',
    () => import('@/features/tools/harici-araclar'),
    'KargoCekmeAraciPage',
  ),
  lazyRoute(
    '/araclar/keepa-magaza',
    () => import('@/features/tools/harici-araclar'),
    'KeepaMagazaAraciPage',
  ),
  // Chrome eklentisi indirme/güncelleme (18 Ekim 2026)
  lazyRoute('/araclar/eklenti', () => import('@/features/tools/eklenti-page'), 'EklentiPage'),
];

const routeTree = rootRoute.addChildren(routes);

export const router = createRouter({
  routeTree,
  // Fareyle üzerine gelince önceden yükle — tıklamada bekleme olmasın
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: ({ error }) => <SayfaHatasi error={error} />,
  /*
   * §19.2 — Gösterge 250 ms sonra beliriyor (`gec-belir`).
   *
   * Rotalar fareyle üzerine gelince önceden indiriliyor, dolayısıyla çoğu
   * geçiş anında bitiyor. Spinner'ı hemen göstermek 50 ms görünüp kaybolan
   * bir titreme üretiyordu — hiç göstermemekten daha rahatsız edici.
   */
  defaultPendingComponent: () => (
    <div className="gec-belir flex items-center justify-center py-24">
      <div className="size-6 animate-spin rounded-full border-2 border-border-strong border-t-accent" />
    </div>
  ),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
